import { useState, useEffect, useCallback } from "react";
import { Bell, ExternalLink, BellRing, BellOff, Loader2, Megaphone } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { pushSupported, isPushEnabled, enablePush, disablePush } from "@/lib/push";

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export default function NotificationBell({ align = "end" }) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [pushOn, setPushOn] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/announcements").then((r) => { setItems(r.data.items || []); setUnread(r.data.unread || 0); }).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => { isPushEnabled().then(setPushOn).catch(() => {}); }, []);

  const onOpenChange = async (o) => {
    setOpen(o);
    if (o && unread > 0) {
      try { await api.post("/announcements/read"); } catch { /* ignore */ }
      setUnread(0);
      setItems((p) => p.map((i) => ({ ...i, read: true })));
    }
  };

  const togglePush = async () => {
    setBusy(true);
    try {
      if (pushOn) { await disablePush(); setPushOn(false); toast.success("Push alerts turned off"); }
      else { await enablePush(); setPushOn(true); toast.success("Push alerts on — you'll get notified"); }
    } catch (e) {
      toast.error(e.message || "Could not update notifications");
    } finally { setBusy(false); }
  };

  const openLink = (url) => {
    if (!url) return;
    if (/^https?:\/\//i.test(url)) window.open(url, "_blank", "noopener");
    else window.location.href = url;
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button data-testid="notification-bell" aria-label="Notifications"
          className="relative w-9 h-9 flex items-center justify-center rounded-lg text-[#9ca3af] hover:text-white hover:bg-[#15151d] transition-colors">
          <Bell size={20} />
          {unread > 0 && (
            <span data-testid="notification-badge"
              className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[#FF5A00] text-[#0a0a0f] text-[10px] font-bold flex items-center justify-center">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align={align} sideOffset={8}
        className="w-[min(92vw,22rem)] p-0 bg-[#15151d] border-[#2a3040] text-white overflow-hidden"
        data-testid="notification-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#2a3040]">
          <div className="font-display font-bold text-sm flex items-center gap-2">
            <Megaphone size={15} className="text-[#FF5A00]" /> Announcements
          </div>
          {pushSupported() && (
            <button data-testid="push-toggle" onClick={togglePush} disabled={busy}
              className={`flex items-center gap-1.5 text-[11px] font-bold rounded-full px-2.5 py-1 transition-colors ${
                pushOn ? "text-[#FF5A00] bg-[#FF5A00]/10 border border-[#FF5A00]/30"
                       : "text-[#9ca3af] bg-[#0a0a0f] border border-[#2a3040] hover:text-white"}`}>
              {busy ? <Loader2 size={12} className="animate-spin" /> : pushOn ? <BellRing size={12} /> : <BellOff size={12} />}
              {pushOn ? "Alerts on" : "Turn on alerts"}
            </button>
          )}
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {items.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-[#6b7280]" data-testid="no-announcements">
              <Bell size={26} className="mx-auto mb-2 opacity-40" />
              No announcements yet.
            </div>
          ) : (
            items.map((a) => (
              <div key={a.id} data-testid={`announcement-${a.id}`}
                className={`px-4 py-3 border-b border-[#2a3040] ${a.read ? "" : "bg-[#FF5A00]/[0.06]"}`}>
                <div className="flex items-start gap-2">
                  {!a.read && <span className="mt-1.5 w-2 h-2 rounded-full bg-[#FF5A00] shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-sm font-bold text-white">{a.title}</div>
                      <div className="text-[10px] text-[#6b7280] shrink-0">{timeAgo(a.created_at)}</div>
                    </div>
                    <p className="text-[13px] text-[#c7ccd6] leading-relaxed mt-0.5 whitespace-pre-wrap break-words">{a.body}</p>
                    {a.link && (
                      <button data-testid={`announcement-link-${a.id}`} onClick={() => openLink(a.link)}
                        className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-[#FF5A00] hover:text-[#FF7A33] transition-colors">
                        {a.link_label || "Open"} <ExternalLink size={12} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
